import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Add as AddIcon, DeleteOutline as DeleteOutlineIcon } from '@material-ui/icons';
import {
    TIER_DISCOUNT_TYPES,
    formatCartTierSubtotalRange,
    getCartDiscountValueError,
    getCartMinSubtotalError,
    sortCartThresholdRows,
} from '../../../constants/cartDiscountTierConfig';

const tierCardStyle = (compact) => ({
    padding: compact ? 14 : 16,
    marginBottom: compact ? 12 : 12,
    borderRadius: 12,
    border: '1px solid #E8E8E8',
    background: '#fff',
});

const mobileNumberInputProps = {
    style: {
        fontSize: 16,
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '0.01em',
    },
};

const mobileAdornmentStyle = {
    fontSize: 16,
    fontWeight: 600,
    color: '#5C5C5C',
    marginRight: 4,
};

const CartDiscountTierThresholdsSection = ({
    thresholds,
    discountType,
    storeCurrency,
    onDiscountTypeChange,
    onThresholdChange,
    onRemoveThreshold,
    onAddThreshold,
    canAddThreshold = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showHelp, setShowHelp] = useState(false);

    const sortedThresholds = useMemo(
        () => sortCartThresholdRows(thresholds),
        [thresholds]
    );

    const currencySymbol = storeCurrency || '$';

    const valueAdornment =
        discountType === 'percentage' ? (
            <InputAdornment position="start">%</InputAdornment>
        ) : (
            <InputAdornment position="start">{currencySymbol}</InputAdornment>
        );

    const subtotalAdornment = (
        <InputAdornment position="start" style={isMobile ? mobileAdornmentStyle : undefined}>
            {currencySymbol}
        </InputAdornment>
    );

    const mobileDiscountAdornment = (
        <InputAdornment position="end" style={mobileAdornmentStyle}>
            {discountType === 'percentage' ? '%' : currencySymbol}
        </InputAdornment>
    );

    const discountTypeLabel = isMobile ? 'Tipo de descuento' : 'Tipo de descuento (todos los niveles)';

    const discountFieldLabel =
        discountType === 'percentage'
            ? isMobile
                ? 'Descuento'
                : 'Descuento (%)'
            : isMobile
              ? 'Descuento'
              : 'Descuento (monto)';

    return (
        <Box>
            <FormControl variant="outlined" fullWidth style={{ marginBottom: isMobile ? 12 : 16 }}>
                <InputLabel id="cart-tier-discount-type">{discountTypeLabel}</InputLabel>
                <Select
                    labelId="cart-tier-discount-type"
                    label={discountTypeLabel}
                    value={discountType}
                    onChange={(e) => onDiscountTypeChange(e.target.value)}
                >
                    {TIER_DISCOUNT_TYPES.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {isMobile ? (
                <Box style={{ marginBottom: 12 }}>
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowHelp((value) => !value)}
                        style={{ textTransform: 'none', padding: 0, minWidth: 0, fontWeight: 600 }}
                    >
                        {showHelp ? 'Ocultar ayuda' : '¿Cómo funcionan los niveles?'}
                    </Button>
                    <Collapse in={showHelp}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 8, lineHeight: 1.45 }}
                        >
                            Cada nivel define desde qué monto total del carrito aplica el descuento.
                            Se evalúa sobre el subtotal sin descuentos. Cada descuento debe ser mayor
                            que el del nivel anterior.
                        </Typography>
                    </Collapse>
                </Box>
            ) : (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    Define desde qué monto total del carrito aplica cada descuento. Se evalúa sobre el
                    subtotal sin descuentos previos. Cada descuento debe ser mayor que el del nivel
                    anterior.
                </Typography>
            )}

            {thresholds.map((threshold, index) => {
                const sortedIndex = sortedThresholds.findIndex(
                    (row) => row.localId === threshold.localId
                );
                const rangeLabel =
                    sortedIndex >= 0
                        ? formatCartTierSubtotalRange(sortedThresholds, sortedIndex, storeCurrency)
                        : '';
                const minSubtotalError =
                    sortedIndex >= 0 ? getCartMinSubtotalError(sortedThresholds, sortedIndex) : null;
                const discountError =
                    sortedIndex >= 0
                        ? getCartDiscountValueError(sortedThresholds, sortedIndex, discountType)
                        : null;

                return (
                    <Paper key={threshold.localId} elevation={0} style={tierCardStyle(isMobile)}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            marginBottom={1.5}
                        >
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600, fontSize: isMobile ? 15 : undefined }}
                            >
                                Nivel {index + 1}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => onRemoveThreshold(threshold.localId)}
                                disabled={thresholds.length <= 1}
                                aria-label={`Eliminar nivel ${index + 1}`}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Grid container spacing={isMobile ? 2 : 2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size={isMobile ? 'small' : 'medium'}
                                    label="Monto mínimo"
                                    type="number"
                                    value={threshold.min_subtotal}
                                    onChange={(e) =>
                                        onThresholdChange(threshold.localId, {
                                            min_subtotal: e.target.value,
                                        })
                                    }
                                    inputProps={{
                                        min: 0,
                                        step: 0.01,
                                        ...(isMobile ? mobileNumberInputProps : {}),
                                    }}
                                    InputProps={{ startAdornment: subtotalAdornment }}
                                    error={Boolean(minSubtotalError)}
                                    helperText={
                                        minSubtotalError ||
                                        (rangeLabel ? `Aplica: ${rangeLabel}` : undefined)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size={isMobile ? 'small' : 'medium'}
                                    label={discountFieldLabel}
                                    type="number"
                                    value={threshold.discount_value}
                                    onChange={(e) =>
                                        onThresholdChange(threshold.localId, {
                                            discount_value: e.target.value,
                                        })
                                    }
                                    inputProps={{
                                        min: 0,
                                        step: discountType === 'percentage' ? 1 : 0.01,
                                        max: discountType === 'percentage' ? 100 : undefined,
                                        ...(isMobile ? mobileNumberInputProps : {}),
                                    }}
                                    InputProps={{
                                        endAdornment: isMobile ? mobileDiscountAdornment : undefined,
                                        startAdornment: isMobile ? undefined : valueAdornment,
                                    }}
                                    error={Boolean(discountError)}
                                    helperText={discountError || undefined}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                );
            })}

            {isMobile && onAddThreshold && (
                <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={onAddThreshold}
                    disabled={!canAddThreshold}
                    startIcon={<AddIcon style={{ fontSize: 20 }} />}
                    style={{
                        marginTop: 8,
                        minHeight: 48,
                        padding: '12px 20px',
                        borderRadius: 12,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 15,
                        borderWidth: 2,
                        borderStyle: 'solid',
                        backgroundColor: '#fff',
                        boxShadow: 'none',
                    }}
                >
                    Agregar nivel
                </Button>
            )}
        </Box>
    );
};

export default CartDiscountTierThresholdsSection;
